// Lesson 3: Umur dan Pekerjaan - Question Configurations

export interface QuestionConfig {
    type: string;
    wordBank?: number[];
    questionSentence?: number[];
    correctAnswer?: number[] | number | string;
    options?: Array<{ id: number; text?: string }>;
    imagePrompt?: string;
    promptText?: string;
}

export interface MatchingPair {
    indonesian: string;
    batak: string;
}

// 1. Vocabulary Introduction (Image + Word)
export const unit1Lesson3VocabIntroConfigs = [
    {
        type: "vocab-intro",
        introWordId: 301, // "Tua" (Umur)
        imagePrompt: "age"
    },
    {
        type: "vocab-intro",
        introWordId: 302, // "Piro" (Berapa)
        imagePrompt: "question_how_much"
    },
    {
        type: "vocab-intro",
        introWordId: 303, // "Tuamu" (Umurmu)
        imagePrompt: "your_age"
    },
    {
        type: "vocab-intro",
        introWordId: 304, // "Pangalus ni bagas" (Pekerjaan)
        imagePrompt: "job"
    },
    {
        type: "vocab-intro",
        introWordId: 305, // "Parguron" (Pelajar)
        imagePrompt: "student"
    },
    {
        type: "vocab-intro",
        introWordId: 306, // "Guru" (Guru)
        imagePrompt: "teacher"
    }
];

// 2. Fill in the Blank
export const unit1Lesson3FillInBlankConfigs: QuestionConfig[] = [
    {
        type: "fill-in-the-blank",
        wordBank: [301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320, 321],
        questionSentence: [308, 320, 999], // "Tuaku dua puluh ______."
        correctAnswer: 307, // taon
        options: [
            { id: 307 }, // taon (correct)
            { id: 314 }, // lima (wrong - number not time unit)
            { id: 302 }, // piro (wrong - question word)
            { id: 301 }  // tua (wrong - age but not time unit)
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320, 321],
        questionSentence: [304, 105, 999], // "Pangalus ni bagas au ______"
        correctAnswer: 306, // guru
        options: [
            { id: 306 }, // guru (correct - specific occupation)
            { id: 307 }, // taon (wrong - time unit not occupation)
            { id: 314 }, // lima (wrong - number not occupation)
            { id: 301 }  // tua (wrong - age concept not occupation)
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320, 321],
        questionSentence: [302, 209, 999], // "Piro do ______?"
        correctAnswer: 303, // tuamu
        options: [
            { id: 303 }, // tuamu (correct - your age)
            { id: 306 }, // guru (wrong - occupation not age)
            { id: 307 }, // taon (wrong - time unit not personal age)
            { id: 302 }  // piro (wrong - question word repetition)
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320, 321],
        questionSentence: [107, 209, 306, 319, 999], // "Ia do guru sampulu ______."
        correctAnswer: 307, // taon
        options: [
            { id: 307 }, // taon (correct)
            { id: 306 }, // guru (wrong - already mentioned)
            { id: 302 }, // piro (wrong - question word not time unit)
            { id: 304 }  // pangalus (wrong - job concept not time unit)
        ]
    },
    {
        type: "fill-in-the-blank",
        wordBank: [301, 302, 303, 304, 305, 306, 307, 308, 309, 310, 311, 312, 313, 314, 315, 316, 317, 318, 319, 320, 321],
        questionSentence: [302, 209, 303, 105, 321, 999], // "Piro do tuamu? Au dua puluh lima ______."
        correctAnswer: 307, // taon
        options: [
            { id: 307 }, // taon (correct)
            { id: 321 }, // dua puluh lima (wrong - number already mentioned)
            { id: 302 }, // piro (wrong - question word not answer)
            { id: 305 }  // parguron (wrong - occupation not time unit)
        ]
    }
];

// 3. Matching
export const unit1Lesson3MatchingWordsConfig = {
    type: "matching-words",
    pairs: [
        { indonesian: "Umur", batak: "ᯖᯮᯀ" },
        { indonesian: "Berapa", batak: "ᯇᯪᯒᯬ" },
        { indonesian: "Umurmu", batak: "ᯖᯮᯀᯔᯮ" },
        { indonesian: "Pekerjaan", batak: "ᯇᯰᯎᯀᯝᯮᯘ ᯉᯪ ᯅᯎᯘ" },
        { indonesian: "Pelajar", batak: "ᯇᯀᯒᯎᯮᯒᯬᯉ᯲" },
        { indonesian: "Guru", batak: "ᯎᯮᯒᯮ" }
    ]
};

// 4. Compose Sentences (Rearrange words)
export const unit1Lesson3ComposeSentenceConfigs: QuestionConfig[] = [
    {
        type: "compose-sentences",
        wordBank: [308, 320, 307], // Tuaku dua puluh taon
        questionSentence: [308, 320, 307],
        correctAnswer: [308, 320, 307]
    },
    {
        type: "compose-sentences",
        wordBank: [304, 105, 306], // Pangalus ni bagas au guru
        questionSentence: [304, 105, 306],
        correctAnswer: [304, 105, 306]
    },
    {
        type: "compose-sentences",
        wordBank: [106, 209, 305, 314, 307], // Ho do parguron lima taon
        questionSentence: [106, 209, 305, 314, 307],
        correctAnswer: [106, 209, 305, 314, 307]
    },
    {
        type: "compose-sentences",
        wordBank: [107, 306, 209, 321, 307], // Ia guru do dua puluh lima taon
        questionSentence: [107, 306, 209, 321, 307],
        correctAnswer: [107, 306, 209, 321, 307]
    },
    {
        type: "compose-sentences",
        wordBank: [302, 209, 303, 105, 315, 307], // Piro do tuamu au onom taon
        questionSentence: [302, 209, 303, 105, 315, 307],
        correctAnswer: [302, 209, 303, 105, 315, 307]
    }
];

// 5. Word Translation (Single word quiz)
export const unit1Lesson3WordTranslationConfigs: QuestionConfig[] = [
    {
        type: "word-translation",
        wordBank: [301],
        correctAnswer: 301, // tua
        options: [
            { id: 301, text: "Umur" },
            { id: 304, text: "Pekerjaan" },
            { id: 307, text: "Tahun" },
            { id: 306, text: "Guru" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [302],
        correctAnswer: 302, // piro
        options: [
            { id: 302, text: "Berapa" },
            { id: 301, text: "Umur" },
            { id: 307, text: "Tahun" },
            { id: 209, text: "adalah" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [305],
        correctAnswer: 305, // parguron
        options: [
            { id: 305, text: "Pelajar" },
            { id: 306, text: "Guru" },
            { id: 322, text: "Pedagang" },
            { id: 323, text: "Pegawai" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [306],
        correctAnswer: 306, // guru
        options: [
            { id: 306, text: "Guru" },
            { id: 305, text: "Pelajar" },
            { id: 304, text: "Pekerjaan" },
            { id: 301, text: "Umur" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [304],
        correctAnswer: 304, // pangalus ni bagas
        options: [
            { id: 304, text: "Pekerjaan" },
            { id: 301, text: "Umur" },
            { id: 307, text: "Tahun" },
            { id: 323, text: "Pegawai" }
        ]
    },
    {
        type: "word-translation",
        wordBank: [315], // onom
        correctAnswer: 315,
        options: [
            { id: 315, text: "Enam" },
            { id: 314, text: "Lima" },
            { id: 316, text: "Tujuh" },
            { id: 317, text: "Delapan" }
        ]
    }
];

// Complete lesson configuration
export const unit1Lesson3QuestionTypes = [
    "vocab-intro",
    "fill-in-the-blank",
    "compose-sentences",
    "matching-words",
    "word-translation"
];
